import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { adminApi, formatApiError } from "@/context/AuthContext";

const MAX = 1600;

// shrink the photo in the browser (phones send 4000 px photos) -> JPEG ~300 kB
const shrink = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, MAX / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * s);
    c.height = Math.round(img.height * s);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("image"))), "image/jpeg", 0.85);
  };
  img.onerror = () => reject(new Error("image"));
  img.src = URL.createObjectURL(file);
});

export const PhotoField = ({ value, onChange, testId = "photo-field" }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choisis une image (JPG, PNG…)."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", await shrink(file), "photo.jpg");
      const { data } = await adminApi.post("/admin/upload", form);
      onChange(data.url);
    } catch (err) {
      toast.error(err.response ? formatApiError(err.response.data?.detail) : "Cette image n'a pas pu être lue.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <div className="w-32 h-24 bg-white/5 border border-white/20 flex items-center justify-center overflow-hidden shrink-0">
        {busy ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
          : value ? <img src={value} alt="Photo du lieu" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
          : <ImagePlus className="w-6 h-6 text-zinc-400" />}
      </div>
      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testId}-pick`}
          className="border border-white/30 text-white uppercase text-xs font-bold tracking-[0.15em] px-4 py-2.5 hover:border-white transition-colors disabled:opacity-50">
          {value ? "Changer la photo" : "Ajouter une photo"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`}
            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400 hover:text-primary">
            <Trash2 className="w-3.5 h-3.5" /> Retirer
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testId}-input`} />
    </div>
  );
};
